import { GalleryVerticalEnd, LayoutGrid, List, PenLine } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { FormError } from '@/components/FormError'
import { MomentCard, MomentRow, MomentTile } from '@/components/MomentCard'
import { FilterButton } from '@/components/Filters'
import { CreateAction } from '@/components/CreateAction'
import { ViewSwitch } from '@/components/ViewSwitch'
import { ModernMoments } from '@/components/modern-moments/ModernMoments'
import { useModernMoments } from '@/components/modern-moments/useModern'
import { Button } from '@/components/ui/button'
import type { Family } from '@/lib/families'
import { usePageFrame } from '@/lib/frame'
import { useMoments } from '@/lib/moments'
import { remember, remembered } from '@/lib/remember'
import { useCurrentSpace } from '@/lib/spaces'
import { PlaceHeader, WithFamily } from './FamilyPage'
import { JustSeeing } from '@/components/JustSeeing'
import { usePosts } from '@/lib/sections'
import { Loader } from '@/components/Loader'
import { column } from '@/components/layout'
import { cn } from '@/lib/utils'
import { BarRow } from '@/components/nav/BarRow'

export const MomentsPage = () => <WithFamily>{(family) => <Moments family={family} />}</WithFamily>

type View = 'feed' | 'cards' | 'list'
const VIEWS: View[] = ['feed', 'cards', 'list']
const VIEW_KEY = 'gatheredfrom:moments-view'

// The circle's moments, newest first: the stories shared to the space you're in, and your own you've kept to
// yourself, which are yours wherever you are. "Only mine" shows just the ones you wrote. Three ways to read them:
// the feed, each story with its photos; cards, a grid of covers; and a list, the most at a glance. The last one
// picked is kept on this device.
function Moments({ family }: { family: Family }) {
  usePageFrame(family.id, { title: 'Moments' })
  const navigate = useNavigate()
  const space = useCurrentSpace(family)
  const [params, setParams] = useSearchParams()
  const mine = params.get('mine') === '1'
  const asked = params.get('view') as View | null
  const view = asked && VIEWS.includes(asked) ? asked : remembered<View>(VIEW_KEY, VIEWS, 'feed')
  const choose = (change: { view?: View; mine?: boolean }) => {
    if (change.view) remember(VIEW_KEY, change.view)
    const query = new URLSearchParams()
    if ((change.view ?? view) !== 'feed') query.set('view', change.view ?? view)
    if (change.mine ?? mine) query.set('mine', '1')
    setParams(query, { replace: true })
  }
  const modern = useModernMoments()
  const moments = useMoments(family.id, mine, space?.id, {}, !modern)
  const list = moments.data?.pages.flatMap((p) => p.moments) ?? []
  const write = () => navigate(`/circle/${family.id}/moments/new`)
  // Nothing yet from anyone: the first moment is welcomed rather than filtered.
  const first = !!moments.data && list.length === 0 && !mine

  const end = useRef<HTMLDivElement>(null)
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = moments
  useEffect(() => {
    const target = end.current
    if (!target || !hasNextPage) return
    const watch = new IntersectionObserver(
      (seen) => {
        if (seen.some((x) => x.isIntersecting) && !isFetchingNextPage) void fetchNextPage()
      },
      { rootMargin: '800px 0px' },
    )
    watch.observe(target)
    return () => watch.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  // Modern lays itself out across the screen like Photos; Traditional reads in one column.
  if (modern)
    return (
      <div className="flex flex-col gap-4 pt-2 pb-8 nav:pt-8">
        {/* The tabs come first; the heading is only for screen readers. */}
        <h1 className="sr-only">Moments</h1>
        <CreateAction
          section="moments"
          title="Write a moment"
          actions={[{ label: 'Write a moment', onSelect: write }]}
        />
        <ModernMoments family={family} spaceId={space?.id} empty={<FirstMoment onWrite={write} />} />
      </div>
    )

  return (
    <div className={cn(column, 'flex flex-col gap-6 pt-6 nav:pt-14')}>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <PlaceHeader family={family} title="Moments" />
      </div>
      <CreateAction section="moments" title="Write a moment" actions={[{ label: 'Write a moment', onSelect: write }]} />
      <FormError error={moments.error} />
      {first ? (
        <FirstMoment onWrite={write} />
      ) : (
        moments.data && (
          <>
            <BarRow className="flex items-center gap-3">
              <ViewSwitch
                label="Show moments as"
                value={view}
                onChange={(v) => choose({ view: v })}
                options={[
                  ['feed', 'Feed', <GalleryVerticalEnd className="size-4" aria-hidden />],
                  ['cards', 'Cards', <LayoutGrid className="size-4" aria-hidden />],
                  ['list', 'List', <List className="size-4" aria-hidden />],
                ]}
              />
              <p className="min-w-0 flex-1 truncate text-right font-medium">{mine ? 'Only mine' : 'Everyone’s'}</p>
              <FilterButton
                title="Filter"
                filters={[
                  {
                    heading: 'Whose',
                    label: 'Whose',
                    value: mine ? 'mine' : 'everyone',
                    usual: 'everyone',
                    options: [
                      ['everyone', 'Everyone’s'],
                      ['mine', 'Only mine'],
                    ],
                    onChange: (w) => choose({ mine: w === 'mine' }),
                  },
                ]}
              />
            </BarRow>
            {list.length === 0 && <p className="text-muted-foreground">You haven’t written any moments yet.</p>}
            {view === 'cards' ? (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4">
                {list.map((moment, i) => (
                  <MomentTile key={moment.id} moment={moment} index={i} />
                ))}
              </div>
            ) : view === 'list' ? (
              <div className="-mx-2 flex flex-col">
                {list.map((moment, i) => (
                  <MomentRow key={moment.id} moment={moment} index={i} />
                ))}
              </div>
            ) : (
              <div className="flex flex-col gap-10 pt-2">
                {list.map((moment) => (
                  <MomentCard key={moment.id} moment={moment} />
                ))}
              </div>
            )}
            <div ref={end} className="flex justify-center">
              {hasNextPage &&
                (isFetchingNextPage ? (
                  <Loader label="Loading more moments" now />
                ) : (
                  <Button variant="outline" onClick={() => void fetchNextPage()}>
                    Show more moments
                  </Button>
                ))}
            </div>
          </>
        )
      )}
    </div>
  )
}

function FirstMoment({ onWrite }: { onWrite: () => void }) {
  const posting = usePosts('moments')
  return (
    <div className="flex flex-col items-center gap-6 rounded-[2.5rem] table-surface px-6 py-12 text-center sm:py-16">
      <span className="grid size-20 place-items-center rounded-full bg-background shadow-paper">
        <PenLine className="size-8" strokeWidth={1.6} aria-hidden />
      </span>
      <div className="flex max-w-md flex-col gap-2">
        <p className="text-title font-semibold tracking-tight text-balance">Every circle has stories worth keeping.</p>
        <p className="text-table-muted">
          A summer, a kitchen, a day you don’t want forgotten. Write it down, add a photo if you have one. It stays
          yours until you choose to share it.
        </p>
      </div>
      {posting ? (
        <Button size="lg" onClick={onWrite}>
          <PenLine aria-hidden /> Write the first moment
        </Button>
      ) : (
        <JustSeeing />
      )}
    </div>
  )
}
